import React, { useState, useEffect } from 'react';
import useFormTheme from '@/hooks/useFormTheme';
import FormThemeToggle from '@/components/FormThemeToggle';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldCheck, ArrowLeft, Phone, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { saveSessionToken } from '@/utils/api';
import { BACKEND_URL } from '@/utils/backendUrl';
import ConsentNote from '@/components/ConsentNote';
import { isMetaWebview } from '@/utils/webview';

const LoginPage = () => {
  const navigate = useNavigate();
  const formTheme = useFormTheme();
  const location = useLocation();
  const returnTo = location.state?.returnTo || null; // resolved after login based on journey_type

  const resolveDestination = (user) => {
    // ss_vertical='couple' is set by CoupleEntryPage/InsightsLandingPage Sign In buttons
    const coupleVertical = localStorage.getItem('ss_vertical') === 'couple';
    localStorage.removeItem('ss_vertical');
    const isInsights =
      coupleVertical ||
      user?.journey_type === 'insights' ||
      localStorage.getItem('ss_journey_type') === 'insights';
    if (isInsights) localStorage.setItem('ss_journey_type', 'insights');
    return returnTo || (isInsights ? '/insights/dashboard' : '/dashboard');
  };
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' | 'mobile'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  // 2FA state (email login)
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [loginToken, setLoginToken] = useState('');
  const [otpCode, setOtpCode] = useState('');

  // Mobile login state
  const [phone, setPhone] = useState('');
  const [mobileStep, setMobileStep] = useState('enter_phone'); // 'enter_phone' | 'enter_otp'
  const [mobileLoginToken, setMobileLoginToken] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const validatePhone = (p) => {
    const d = p.replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) return /^91[6-9]\d{9}$/.test(d);
    return /^[6-9]\d{9}$/.test(d);
  };
  const normalisePhone = (p) => {
    const d = p.replace(/\D/g, '');
    return d.length === 12 && d.startsWith('91') ? `+${d}` : `+91${d}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        formData,
        { withCredentials: true }
      );

      // Check if 2FA is required
      if (response.data.status === '2fa_required') {
        setLoginToken(response.data.login_token);
        setTwoFAStep(true);
        toast.info('Verification code sent to your email');
        setLoading(false);
        return;
      }

      saveSessionToken(response.data);
      const user = response.data.user;
      toast.success('Login successful!', { duration: 2000 });

      // Check if terms need to be accepted (existing user, outdated/missing terms)
      if (!user.terms_accepted) {
        navigate('/accept-terms', {
          state: { returnTo: resolveDestination(user) },
          replace: true,
        });
        return;
      }

      navigate(resolveDestination(user), { state: { user } });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = (Array.isArray(detail) ? detail[0]?.msg : detail) || error?.message || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/verify-2fa`,
        { login_token: loginToken, otp: otpCode },
        { withCredentials: true }
      );
      saveSessionToken(response.data);
      const user = response.data.user;
      toast.success('Login successful!', { duration: 2000 });

      if (!user.terms_accepted) {
        navigate('/accept-terms', { state: { returnTo: resolveDestination(user) }, replace: true });
        return;
      }
      navigate(resolveDestination(user), { state: { user } });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = (Array.isArray(detail) ? detail[0]?.msg : detail) || error?.message || 'Verification failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMobileSendOtp = async (e) => {
    e.preventDefault();
    if (!validatePhone(phone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/phone/login-send-otp`, {
        phone: normalisePhone(phone),
      });
      setMobileLoginToken(res.data.login_token);
      setMobileStep('enter_otp');
      setResendCooldown(30);
      toast.success('OTP sent to your mobile number!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Could not send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileVerifyOtp = async (e) => {
    e.preventDefault();
    if (mobileOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/phone/login-verify-otp`,
        { login_token: mobileLoginToken, otp: mobileOtp },
        { withCredentials: true }
      );
      saveSessionToken(res.data);
      const user = res.data.user;
      toast.success('Login successful!', { duration: 2000 });
      if (!user.terms_accepted) {
        navigate('/accept-terms', { state: { returnTo: resolveDestination(user) }, replace: true });
        return;
      }
      navigate(resolveDestination(user), { state: { user } });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Incorrect OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/phone/login-send-otp`, {
        phone: normalisePhone(phone),
      });
      setMobileLoginToken(res.data.login_token);
      setMobileOtp('');
      setResendCooldown(30);
      toast.success('OTP resent!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Could not resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // The backend /google/start endpoint owns the OAuth redirect: direct
    // Google OAuth when configured, legacy Emergent broker otherwise.
    try {
      // Persist returnTo through the OAuth full-page redirect so AuthCallback can restore it
      if (returnTo) localStorage.setItem('ss_return_to', returnTo);
      const redirectUrl = window.location.origin + (returnTo || '/');
      window.location.href = `${BACKEND_URL}/api/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      toast.error('Google sign-in could not start. Please try again or use email and password.');
    }
  };

  // ── Figma "Login" (854:1076) visual tokens ──────────────────────────────
  const fieldStyle = {
    background: 'hsl(var(--input))',
    border: '1px solid hsl(var(--border))',
    color: 'hsl(var(--foreground))',
    filter: 'drop-shadow(1px 1px 6px rgba(3,4,4,0.04))',
  };
  const fieldClass = 'h-[52px] rounded-xl px-3 py-[9px] text-sm leading-[1.6] tracking-[0.21px] bg-transparent border-0 shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary';
  const labelClass = 'text-sm leading-5 font-medium';
  const labelStyle = { color: 'hsl(var(--foreground) / 0.85)' };
  const primaryBtn = 'w-full h-12 rounded-xl px-5 py-3 text-base leading-6 font-medium hover:opacity-90';
  const primaryStyle = { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' };
  const segment = (active) => ({
    background: active ? 'hsl(var(--primary))' : 'hsl(var(--background))',
    color: active ? 'hsl(var(--background))' : 'hsl(var(--primary))',
    border: '1px solid hsl(var(--primary))',
  });

  return (
    <div className="h-screen flex gap-8 p-4 lg:p-8 bg-background overflow-hidden">
      <FormThemeToggle theme={formTheme.theme} onToggle={formTheme.toggle} />
      {/* Left: Form panel (854:1078) */}
      <div className="w-full lg:flex-1 h-full overflow-y-auto px-2 sm:px-8 xl:px-[120px]">
        <div className="min-h-full flex flex-col items-center justify-center py-8">
        <div className="w-full max-w-[440px] flex flex-col gap-12">

          {twoFAStep ? (
            /* ── 2FA Verification Step ── */
            <form onSubmit={handleVerify2FA} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'rgba(241,178,0,0.1)', border: '1px solid rgba(241,178,0,0.3)' }}>
                  <ShieldCheck className="w-7 h-7 text-primary" />
                </div>
                <h1 className="font-heading text-[32px] sm:text-[40px] leading-tight sm:leading-[48px] font-medium tracking-[-0.4px] text-primary">Two-Factor Verification</h1>
                <p className="text-lg leading-7 text-foreground">Enter the 6-digit code sent to your email</p>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="otp" className={labelClass} style={labelStyle}>Verification Code</Label>
                <div className="rounded-xl" style={fieldStyle}>
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className={`${fieldClass} text-center text-2xl tracking-[0.3em] font-mono`}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    required
                    data-testid="otp-input"
                  />
                </div>
              </div>
              <Button type="submit" className={primaryBtn} style={primaryStyle} disabled={loading || otpCode.length !== 6}>
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : 'Verify & Sign In'}
              </Button>
              <button
                type="button"
                onClick={() => { setTwoFAStep(false); setOtpCode(''); setLoginToken(''); }}
                className="w-full flex items-center justify-center gap-1.5 text-base font-medium text-primary underline"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </form>

          ) : mobileStep === 'enter_otp' && loginMethod === 'mobile' ? (
            /* ── Mobile OTP Entry ── */
            <form onSubmit={handleMobileVerifyOtp} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'rgba(241,178,0,0.1)', border: '1px solid rgba(241,178,0,0.3)' }}>
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <h1 className="font-heading text-[32px] sm:text-[40px] leading-tight sm:leading-[48px] font-medium tracking-[-0.4px] text-primary">Verify Mobile</h1>
                <p className="text-lg leading-7 text-foreground">
                  Enter the 6-digit code sent to <span className="font-medium">{normalisePhone(phone).replace(/(\+91)(\d{2})\d{6}(\d{2})/, '$1 $2******$3')}</span>
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="mobile-otp" className={labelClass} style={labelStyle}>OTP</Label>
                <div className="rounded-xl" style={fieldStyle}>
                  <Input
                    id="mobile-otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    className={`${fieldClass} text-center text-xl tracking-[0.4em] font-mono`}
                    value={mobileOtp}
                    onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                  />
                </div>
              </div>
              <Button type="submit" className={primaryBtn} style={primaryStyle} disabled={loading || mobileOtp.length !== 6}>
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : 'Verify & Sign In'}
              </Button>
              <div className="text-center">
                {resendCooldown > 0 ? (
                  <p className="text-sm" style={{ color: '#aaaaaa' }}>Resend OTP in {resendCooldown}s</p>
                ) : (
                  <button type="button" onClick={handleMobileResend} className="text-base font-medium text-primary underline">
                    Resend OTP
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setMobileStep('enter_phone'); setMobileOtp(''); }}
                className="w-full flex items-center justify-center gap-1.5 text-base font-medium text-primary underline"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </form>

          ) : (
          <>
            {/* Intro (854:1080) */}
            <div className="flex flex-col gap-2">
              <h1 className="font-heading text-[32px] sm:text-[40px] leading-tight sm:leading-[48px] font-medium tracking-[-0.4px] text-primary">Welcome Back</h1>
              <p className="text-lg leading-7 text-foreground">Sign in to continue your journey</p>
            </div>

            {/* ── Segmented Buttons (854:1083) ── */}
            <div className="flex items-start w-full">
              <button
                type="button"
                onClick={() => setLoginMethod('email')}
                className="flex-1 h-12 px-4 py-[7px] text-sm leading-5 font-medium rounded-l-xl flex items-center justify-center transition-colors"
                style={{ ...segment(loginMethod === 'email'), borderRightWidth: 0.5 }}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('mobile')}
                className="flex-1 h-12 px-4 py-[7px] text-sm leading-5 font-medium rounded-r-xl flex items-center justify-center transition-colors"
                style={{ ...segment(loginMethod === 'mobile'), borderLeftWidth: 0.5 }}
              >
                OTP
              </button>
            </div>

            {loginMethod === 'email' ? (
              /* ── Email + Password Form (854:1088) ── */
              <form onSubmit={handleSubmit} className="flex flex-col gap-6 items-end w-full">
                <div className="flex flex-col gap-6 w-full">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="email" className={labelClass} style={labelStyle}>Email</Label>
                    <div className="rounded-xl" style={fieldStyle}>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Email Address"
                        className={fieldClass}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="password" className={labelClass} style={labelStyle}>Password</Label>
                    <div className="relative rounded-xl" style={fieldStyle}>
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        className={`${fieldClass} pr-10`}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                        style={{ color: '#aaaaaa' }}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <Link to="/forgot-password" className="text-base leading-6 font-medium text-primary underline" data-testid="forgot-password-link">
                  Forgot Password?
                </Link>
                <Button type="submit" className={primaryBtn} style={primaryStyle} disabled={loading} data-testid="login-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : 'Sign In'}
                </Button>
              </form>
            ) : (
              /* ── Mobile Number Form ── */
              <form onSubmit={handleMobileSendOtp} className="flex flex-col gap-6 w-full">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="phone" className={labelClass} style={labelStyle}>Mobile Number</Label>
                  <div className="rounded-xl" style={fieldStyle}>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit mobile number"
                      className={fieldClass}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {phone && !validatePhone(phone) && (
                    <p className="text-xs mt-1" style={{ color: '#fb3748' }}>Enter a valid Indian mobile number (10 digits, starting with 6–9)</p>
                  )}
                </div>
                <Button type="submit" className={primaryBtn} style={primaryStyle} disabled={loading || !validatePhone(phone)}>
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending OTP...</> : 'Send OTP'}
                </Button>
              </form>
            )}

            {/* Google — offered after the form per this Figma layout (still fully
                functional, unchanged handler). Hidden in Meta webviews where
                Google blocks OAuth. */}
            {!isMetaWebview() && (
            <div className="flex flex-col gap-6 w-full">
              <div className="flex gap-4 items-center justify-center py-2.5 w-full">
                <span className="flex-1 h-px" style={{ background: '#555555' }} />
                <span className="text-base leading-none tracking-[0.16px]" style={{ color: '#aaaaaa' }}>Or</span>
                <span className="flex-1 h-px" style={{ background: '#555555' }} />
              </div>
              <button
                type="button"
                className="w-full flex gap-4 items-center justify-center px-[9px] py-3 rounded-xl hover:opacity-90 transition-opacity"
                style={{ background: '#f3f9fa' }}
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <img src="/ic-google.svg" alt="" aria-hidden="true" className="w-7 h-7" draggable={false} />
                <span className="text-base leading-none tracking-[0.16px]" style={{ color: '#313957' }}>Sign in with Google</span>
              </button>
            </div>
            )}

            <ConsentNote action="signing in" />

            <p className="text-center text-base leading-6 font-medium">
              <span style={{ color: '#c6c6c6' }}>Don't you have an account? </span>
              <Link to="/register" className="text-primary underline">Sign up</Link>
            </p>
          </>
          )}
        </div>
        <p className="mt-12 text-base leading-6 text-center whitespace-nowrap" style={{ color: 'hsl(var(--muted-foreground))' }}>
          © 2026 ALL RIGHTS RESERVED
        </p>
        </div>
      </div>

      {/* Right: Art (854:1123) */}
      <div className="hidden lg:flex lg:flex-1 h-full items-start">
        <div className="w-full h-full rounded-3xl overflow-hidden" style={{ background: '#fea91a' }}>
          <img
            src="/auth-couple.webp"
            alt="Couple who found compatibility through SoulSathiya"
            className="w-full h-full object-cover object-bottom"
            loading="lazy"
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
